import React, { useState } from "react";
import { Check, X as IconX, ArrowRight, CreditCard, ShieldCheck } from "@phosphor-icons/react";
import { createCheckoutSession } from "../lib/api";
import { toast } from "sonner";
import RazorpayButton from "./RazorpayButton";

/**
 * Feature matrix — the order here defines the rows shown on every card.
 */
const FEATURE_ROWS = [
  { key: "live", label: "Live trading support · 3 days a week" },
  { key: "classes_rec", label: "Live classes + recordings" },
  { key: "whatsapp", label: "WhatsApp community access" },
  { key: "daily_levels", label: "Daily Nifty / Bank Nifty / Midcap levels" },
  {
    key: "options_chart",
    label:
      "Index levels + options table for study · Market Profile applied to options",
  },
  {
    key: "mcx",
    label: "MCX live · 2 days a week",
  },
  { key: "eod", label: "EOD Analysis & Level-Drawing" },
  { key: "previous", label: "Previous Batch Recordings" },
  { key: "monthly", label: "Monthly 1-on-1 Mentorship Sessions" },
  { key: "pnl", label: "P&L Review System" },
  { key: "setup", label: "Setup Compatibility Reviews" },
  {
    key: "practical",
    label:
      "Practical Phase (Mark levels · Paper-trade · Back-test assignment)",
  },
];

const PACKAGES = [
  {
    id: "pro",
    badge: "MOST POPULAR",
    title: "Pro",
    subtitle: "Advanced mentorship · Theory + Practical assignment",
    duration: "6 months",
    price: 40000,
    perDay: 333, // 6 months × ~20 trading days = 120 → ₹333/day
    yearPrice: 50000,
    yearPerDay: 208, // 12 months × ~20 = 240 → ₹208/day
    accent: "#D9381E",
    highlight: true,
    razorpayId: "",
    features: {
      live: true,
      classes_rec: true,
      whatsapp: true,
      daily_levels: true,
      options_chart: true,
      mcx: true,
      eod: true,
      previous: true,
      monthly: true,
      pnl: true,
      setup: true,
      practical: true,
    },
    cta: "Get Pro",
  },
  {
    id: "core",
    badge: "CORE",
    title: "Core",
    subtitle: "Basics mentorship · Market Profile & Order Flow",
    duration: "3 months",
    price: 30000,
    perDay: 500, // 3 months × 20 trading days = 60 → ₹500/day
    accent: "#8A8A8A",
    razorpayId: "",
    features: {
      live: true,
      classes_rec: true,
      whatsapp: true,
      daily_levels: true,
      options_chart: true,
      mcx: true,
      eod: true,
      previous: false,
      monthly: false,
      pnl: false,
      setup: false,
      practical: false,
    },
    cta: "Get Core",
  },
  {
    id: "starter",
    badge: "STARTER",
    title: "Starter",
    subtitle: "Recordings-first · with live trading support 3 days a week",
    duration: "3 months + recordings",
    price: 20000,
    perDay: 333, // 3 months × 20 trading days = 60 → ₹333/day
    accent: "#A57438",
    razorpayId: "",
    features: {
      live: true,
      classes_rec: "recordings", // special: recordings only, no live classes
      whatsapp: true,
      daily_levels: true,
      options_chart: true,
      mcx: true,
      eod: true,
      previous: false,
      monthly: false,
      pnl: false,
      setup: false,
      practical: false,
    },
    cta: "Get Starter",
  },
];

export default function Packages() {
  const [loading, setLoading] = useState(null);

  const pay = async (pkgId) => {
    setLoading(pkgId);
    try {
      const res = await createCheckoutSession({
        package_id: pkgId,
        origin_url: window.location.origin,
        name: "",
        email: "",
        phone: "",
      });
      if (res?.url) {
        window.location.href = res.url;
      } else {
        toast.error("Could not start checkout. Try WhatsApp.");
      }
    } catch (err) {
      toast.error(
        err?.response?.data?.detail || "Payment could not start. Try WhatsApp."
      );
    } finally {
      setLoading(null);
    }
  };

  const scrollToEnroll = () => {
    document.getElementById("enroll")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      data-testid="packages-section"
      id="packages"
      className="py-20 lg:py-28 border-t border-[#D4D0C5] bg-[#EAE8DF]"
    >
      <div className="max-w-[1320px] mx-auto px-5 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
          <div>
            <div className="font-mono-acc text-[10px] tracking-[0.3em] uppercase text-[#D9381E] mb-4">
              07 / PACKAGES
            </div>
            <h2 className="font-display font-black text-4xl lg:text-6xl tracking-tighter leading-[0.95]">
              Pick your
              <br />
              path.
            </h2>
          </div>
          <p className="font-body text-[#4A4A4A] max-w-md">
            Three carefully tiered packages. <strong>Every</strong> tier
            includes daily Nifty / Bank Nifty / Midcap levels, WhatsApp
            community access, EOD analysis, MCX (2 days a week) and live
            trading support (3 days a week). Pick the depth of mentorship
            that fits where you are today.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {PACKAGES.map((pkg) => {
            const isHighlight = pkg.highlight;
            return (
              <div
                key={pkg.id}
                data-testid={`package-${pkg.id}`}
                className={`relative flex flex-col border bg-[#F4F3ED] p-7 lg:p-9 hover-brutal ${
                  isHighlight
                    ? "border-[#0A0A0A] lg:-translate-y-2 lg:shadow-[8px_8px_0_#0A0A0A]"
                    : "border-[#0A0A0A]"
                }`}
              >
                <div className="flex items-center justify-between mb-5">
                  <span
                    className="font-mono-acc text-[10px] tracking-[0.3em] uppercase px-2.5 py-1"
                    style={{
                      backgroundColor: isHighlight ? "#D9381E" : "transparent",
                      color: isHighlight ? "#fff" : pkg.accent,
                      border: isHighlight
                        ? "1px solid #D9381E"
                        : `1px solid ${pkg.accent}`,
                    }}
                  >
                    {pkg.badge}
                  </span>
                  <span className="font-mono-acc text-[10px] tracking-[0.25em] text-[#4A4A4A] uppercase">
                    {pkg.duration}
                  </span>
                </div>

                <h3 className="font-display font-black text-4xl lg:text-5xl tracking-tighter leading-none">
                  {pkg.title}
                </h3>
                <p className="font-body text-sm text-[#4A4A4A] mt-2 mb-7 min-h-[42px]">
                  {pkg.subtitle}
                </p>

                <div className="mb-6 pb-6 border-b border-[#D4D0C5]">
                  <div className="flex items-baseline gap-2">
                    <span className="font-display font-black text-4xl lg:text-5xl tracking-tighter">
                      ₹{pkg.price.toLocaleString("en-IN")}
                    </span>
                  </div>
                  <div className="font-mono-acc text-[10px] tracking-[0.3em] text-[#4A4A4A] uppercase mt-2">
                    One-time · {pkg.duration}
                  </div>
                  {pkg.perDay && (
                    <div
                      data-testid={`per-day-${pkg.id}`}
                      className="font-mono-acc text-[11px] tracking-[0.18em] text-[#D9381E] uppercase mt-2 font-bold"
                    >
                      ≈ ₹{pkg.perDay.toLocaleString("en-IN")} / trading day
                    </div>
                  )}
                  {pkg.yearPrice && (
                    <div
                      data-testid="pro-year-upgrade"
                      className="mt-3 border border-[#FFB703] bg-[#FFF6E0] px-3 py-2"
                    >
                      <div className="font-mono-acc text-[9px] tracking-[0.25em] uppercase text-[#B37A00] mb-0.5">
                        Optional · 1-year upgrade
                      </div>
                      <div className="font-body text-[12px] leading-snug text-[#4A2E00]">
                        Extend to a <strong>full year</strong> +{" "}
                        <strong>12 monthly 1-on-1 mentor calls</strong> at{" "}
                        <span className="font-mono-acc font-bold text-[#0A0A0A]">
                          ₹50,000
                        </span>{" "}
                        <span className="font-mono-acc text-[10px] text-[#B37A00]">
                          (≈ ₹{pkg.yearPerDay}/day)
                        </span>{" "}
                        — WhatsApp us to add on.
                      </div>
                    </div>
                  )}
                </div>

                <ul className="space-y-2.5 mb-7 flex-1">
                  {FEATURE_ROWS.map((row) => {
                    const val = pkg.features[row.key];
                    // Special rendering: "recordings" = has, but shows as "Recordings only"
                    const isRecOnly =
                      val === "recordings" && row.key === "classes_rec";
                    const has = Boolean(val);
                    const label = isRecOnly
                      ? "Recordings only · no live classes"
                      : row.label;
                    return (
                      <li
                        key={row.key}
                        className={`flex items-start gap-2.5 font-body text-[13px] leading-snug ${
                          has
                            ? "text-[#0A0A0A]"
                            : "text-[#A1A1AA] line-through decoration-[#D4D0C5]"
                        }`}
                      >
                        <span
                          className={`shrink-0 mt-0.5 ${
                            has ? "text-[#D9381E]" : "text-[#D4D0C5]"
                          }`}
                        >
                          {has ? (
                            <Check size={15} weight="bold" />
                          ) : (
                            <IconX size={15} weight="bold" />
                          )}
                        </span>
                        <span>{label}</span>
                      </li>
                    );
                  })}
                </ul>

                {/* Razorpay (UPI/INR-first) */}
                <div className="mb-3">
                  <div className="font-mono-acc text-[10px] tracking-[0.25em] uppercase text-[#4A4A4A] mb-2 text-center">
                    Pay via UPI / Cards · Razorpay
                  </div>
                  <RazorpayButton
                    buttonId={pkg.razorpayId}
                    amount={pkg.price}
                    packageLabel={pkg.title}
                  />
                </div>

                <div className="flex items-center gap-3 my-2">
                  <div className="flex-1 h-px bg-[#D4D0C5]" />
                  <span className="font-mono-acc text-[10px] tracking-[0.3em] text-[#4A4A4A] uppercase">
                    OR
                  </span>
                  <div className="flex-1 h-px bg-[#D4D0C5]" />
                </div>

                {/* Stripe (international cards) */}
                <button
                  data-testid={`pay-${pkg.id}`}
                  onClick={() => pay(pkg.id)}
                  disabled={loading === pkg.id}
                  className={`w-full inline-flex items-center justify-center gap-3 py-3.5 font-body text-sm uppercase tracking-[0.22em] transition-colors disabled:opacity-60 ${
                    isHighlight
                      ? "bg-[#0A0A0A] text-[#F4F3ED] border border-[#0A0A0A] hover:bg-[#D9381E] hover:border-[#D9381E]"
                      : "bg-[#0A0A0A] text-[#F4F3ED] border border-[#0A0A0A] hover:bg-[#D9381E] hover:border-[#D9381E]"
                  }`}
                >
                  <CreditCard size={16} weight="bold" />
                  {loading === pkg.id
                    ? "Redirecting…"
                    : "Pay via Stripe (Intl)"}
                  {loading !== pkg.id && <ArrowRight size={14} weight="bold" />}
                </button>

                <button
                  data-testid={`talk-${pkg.id}`}
                  onClick={scrollToEnroll}
                  className="mt-2 w-full text-center font-mono-acc text-[10px] tracking-[0.22em] uppercase text-[#4A4A4A] hover:text-[#D9381E]"
                >
                  Or talk to us first →
                </button>
              </div>
            );
          })}
        </div>

        <div className="mt-6 flex items-center justify-center gap-2">
          <ShieldCheck size={14} weight="duotone" className="text-[#D9381E]" />
          <p className="font-mono-acc text-[10px] tracking-[0.25em] text-[#4A4A4A] uppercase text-center">
            Secure payment · Razorpay (UPI / Netbanking / Cards / EMI) · Stripe (Intl)
          </p>
        </div>
      </div>
    </section>
  );
}
